import { supabase } from '@shared/lib/supabase';
import { getCurrentProfileId } from '@shared/lib/profile-id';
import { type TablesUpdate } from '@shared/lib/database.types';
import { type SetDefaults, type WorkoutRepository } from '../application/workout.repository';
import {
  type SessionExerciseGroup,
  type SessionType,
  type SetPatch,
  type StartAdHocInput,
  type StartBackfillInput,
  type StartFromProgramDayInput,
  type WorkoutSession,
  type WorkoutSessionFull,
  type WorkoutSet,
} from '../domain/workout';
import { parsePlannedReps } from '../domain/planned-reps';

const SESSION_COLS =
  'id, user_id, program_id, program_day_id, title, session_type, started_at, completed_at, notes';
const SET_COLS =
  'id, workout_session_id, exercise_id, order_index, set_index, weight, reps, duration_seconds, distance_meters, rpe, completed, skipped, started_at, completed_at';

interface SessionRow {
  id: string;
  user_id: string;
  program_id: string | null;
  program_day_id: string | null;
  title: string | null;
  session_type: string;
  started_at: string;
  completed_at: string | null;
  notes: string | null;
}

interface SetRow {
  id: string;
  workout_session_id: string;
  exercise_id: string;
  order_index: number;
  set_index: number;
  weight: number | null;
  reps: number | null;
  duration_seconds: number | null;
  distance_meters: number | null;
  rpe: number | null;
  completed: boolean;
  skipped: boolean;
  started_at: string | null;
  completed_at: string | null;
}

function asSessionType(value: string): SessionType {
  return value === 'planned' || value === 'ad_hoc' || value === 'template_based' ? value : 'ad_hoc';
}

function sessionToDomain(row: SessionRow): WorkoutSession {
  return {
    id: row.id,
    userId: row.user_id,
    programId: row.program_id,
    programDayId: row.program_day_id,
    title: row.title,
    sessionType: asSessionType(row.session_type),
    startedAt: row.started_at,
    completedAt: row.completed_at,
    notes: row.notes,
  };
}

function setToDomain(row: SetRow): WorkoutSet {
  return {
    id: row.id,
    workoutSessionId: row.workout_session_id,
    exerciseId: row.exercise_id,
    orderIndex: row.order_index,
    setIndex: row.set_index,
    weight: row.weight,
    reps: row.reps,
    durationSeconds: row.duration_seconds,
    distanceMeters: row.distance_meters,
    rpe: row.rpe,
    completed: row.completed,
    skipped: row.skipped,
    startedAt: row.started_at,
    completedAt: row.completed_at,
  };
}

const ALLOWED_METRICS = ['weight', 'reps', 'duration', 'distance'] as const;
type AllowedMetric = (typeof ALLOWED_METRICS)[number];

function asMetrics(value: unknown): readonly AllowedMetric[] {
  if (typeof value !== 'object' || value === null) return ['weight', 'reps'];
  const m = (value as { metrics?: unknown }).metrics;
  if (!Array.isArray(m)) return ['weight', 'reps'];
  return m.filter((x): x is AllowedMetric =>
    (ALLOWED_METRICS as readonly string[]).includes(x as string),
  );
}

// parsePlannedReps lives in domain/planned-reps.ts now so it can be unit-tested
// in isolation. Re-imported below; behaviour unchanged.

export const supabaseWorkoutRepository: WorkoutRepository = {
  async listMine(): Promise<WorkoutSession[]> {
    const userId = await getCurrentProfileId();
    const { data, error } = await supabase
      .from('workout_sessions')
      .select(SESSION_COLS)
      .eq('user_id', userId)
      .order('started_at', { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    return data.map(sessionToDomain);
  },

  async listForUser(profileId: string): Promise<WorkoutSession[]> {
    const { data, error } = await supabase
      .from('workout_sessions')
      .select(SESSION_COLS)
      .eq('user_id', profileId)
      .order('started_at', { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    return data.map(sessionToDomain);
  },

  async getActive(): Promise<WorkoutSession | null> {
    const userId = await getCurrentProfileId();
    const { data, error } = await supabase
      .from('workout_sessions')
      .select(SESSION_COLS)
      .eq('user_id', userId)
      .is('completed_at', null)
      .order('started_at', { ascending: false })
      .limit(1);
    if (error) throw new Error(error.message);
    return data.length > 0 ? sessionToDomain(data[0]!) : null;
  },

  async getFull(sessionId: string): Promise<WorkoutSessionFull> {
    const { data: sessionData, error: sessionError } = await supabase
      .from('workout_sessions')
      .select(SESSION_COLS)
      .eq('id', sessionId)
      .single();
    if (sessionError) throw new Error(sessionError.message);

    const { data: setsData, error: setsError } = await supabase
      .from('workout_sets')
      .select(SET_COLS)
      .eq('workout_session_id', sessionId)
      .order('order_index', { ascending: true })
      .order('set_index', { ascending: true });
    if (setsError) throw new Error(setsError.message);

    const exerciseIds = Array.from(new Set(setsData.map((s) => s.exercise_id)));
    const exercisesById = new Map<
      string,
      {
        name: string;
        category: string;
        equipment: string | null;
        metrics: readonly AllowedMetric[];
      }
    >();
    if (exerciseIds.length > 0) {
      const { data: exData, error: exError } = await supabase
        .from('exercises')
        .select('id, name, category, equipment, metric_schema')
        .in('id', exerciseIds);
      if (exError) throw new Error(exError.message);
      for (const e of exData) {
        exercisesById.set(e.id, {
          name: e.name,
          category: e.category,
          equipment: e.equipment,
          metrics: asMetrics(e.metric_schema),
        });
      }
    }

    // If the session was started from a program day, pull the per-slot rest
    // target so the guided RestView can show a goal + cue. Empty map for
    // ad-hoc / template-based sessions.
    const restByExerciseId = new Map<string, number>();
    if (sessionData.program_day_id && exerciseIds.length > 0) {
      const { data: slotData, error: slotError } = await supabase
        .from('program_exercise_slots')
        .select('exercise_id, planned_rest_seconds')
        .eq('program_day_id', sessionData.program_day_id)
        .in('exercise_id', exerciseIds);
      if (slotError) throw new Error(slotError.message);
      for (const row of slotData) {
        if (row.planned_rest_seconds !== null && row.planned_rest_seconds > 0) {
          restByExerciseId.set(row.exercise_id, row.planned_rest_seconds);
        }
      }
    }

    // Group sets by exerciseId, preserving the lowest order_index for sort.
    const groupsMap = new Map<string, { orderIndex: number; sets: WorkoutSet[] }>();
    for (const s of setsData) {
      const set = setToDomain(s);
      const g = groupsMap.get(set.exerciseId);
      if (g) {
        g.sets.push(set);
        if (set.orderIndex < g.orderIndex) g.orderIndex = set.orderIndex;
      } else {
        groupsMap.set(set.exerciseId, { orderIndex: set.orderIndex, sets: [set] });
      }
    }

    const groups: SessionExerciseGroup[] = Array.from(groupsMap.entries())
      .map(([exerciseId, { orderIndex, sets }]) => {
        const ex = exercisesById.get(exerciseId);
        return {
          exerciseId,
          exerciseName: ex?.name ?? 'Удалённое упражнение',
          category: ex?.category ?? 'strength',
          equipment: ex?.equipment ?? null,
          metrics: ex?.metrics ?? ['weight', 'reps'],
          orderIndex,
          plannedRestSeconds: restByExerciseId.get(exerciseId) ?? null,
          sets,
        } satisfies SessionExerciseGroup;
      })
      .sort((a, b) => a.orderIndex - b.orderIndex);

    return { ...sessionToDomain(sessionData), groups };
  },

  async startAdHoc(input: StartAdHocInput): Promise<WorkoutSession> {
    // Default to current user; coach-mode passes a managed ghost's profile id
    // so the session lands under that athlete (RLS gates this — see migration
    // 0006 "workout_sessions: insert own or managed").
    const userId = input.targetUserId ?? (await getCurrentProfileId());
    const { data, error } = await supabase
      .from('workout_sessions')
      .insert({
        user_id: userId,
        title: input.title && input.title.trim() !== '' ? input.title.trim() : null,
        session_type: 'ad_hoc',
      })
      .select(SESSION_COLS)
      .single();
    if (error) throw new Error(error.message);
    return sessionToDomain(data);
  },

  async startBackfill(input: StartBackfillInput): Promise<WorkoutSession> {
    const userId = await getCurrentProfileId();
    // Anchor started_at to noon on the chosen day so it lands on the right
    // calendar square regardless of viewer timezone. Leave completed_at null
    // so the session is editable (user adds exercises/sets); finish() picks
    // a same-day completed_at when the user wraps up.
    const startedAt = new Date(`${input.date}T12:00:00`).toISOString();
    const { data, error } = await supabase
      .from('workout_sessions')
      .insert({
        user_id: userId,
        title: input.title && input.title.trim() !== '' ? input.title.trim() : null,
        session_type: 'ad_hoc',
        started_at: startedAt,
      })
      .select(SESSION_COLS)
      .single();
    if (error) throw new Error(error.message);
    return sessionToDomain(data);
  },

  async startFromProgramDay(input: StartFromProgramDayInput): Promise<WorkoutSession> {
    const userId = await getCurrentProfileId();

    // 1. Create session
    const { data: session, error: sessionError } = await supabase
      .from('workout_sessions')
      .insert({
        user_id: userId,
        program_id: input.programId,
        program_day_id: input.programDayId,
        title: input.title?.trim() ? input.title.trim() : null,
        session_type: 'planned',
      })
      .select(SESSION_COLS)
      .single();
    if (sessionError) throw new Error(sessionError.message);

    // 2. Pre-fill one set per planned slot with planned defaults.
    const { data: slots, error: slotsError } = await supabase
      .from('program_exercise_slots')
      .select(
        'exercise_id, order_index, planned_sets, planned_reps, planned_weight, planned_duration_seconds, planned_distance_meters',
      )
      .eq('program_day_id', input.programDayId)
      .order('order_index', { ascending: true });
    if (slotsError) throw new Error(slotsError.message);

    if (slots.length > 0) {
      const rows = slots.flatMap((slot) => {
        const sets = slot.planned_sets ?? 1;
        const repsTarget = parsePlannedReps(slot.planned_reps);
        return Array.from({ length: Math.max(1, sets) }, (_, i) => ({
          workout_session_id: session.id,
          exercise_id: slot.exercise_id,
          order_index: slot.order_index,
          set_index: i + 1,
          weight: slot.planned_weight,
          reps: repsTarget,
          duration_seconds: slot.planned_duration_seconds,
          distance_meters: slot.planned_distance_meters,
          completed: false,
        }));
      });
      const { error: insertError } = await supabase.from('workout_sets').insert(rows);
      if (insertError) throw new Error(insertError.message);
    }

    return sessionToDomain(session);
  },

  async finish(sessionId: string, completedAt?: string): Promise<WorkoutSession> {
    const { data, error } = await supabase
      .from('workout_sessions')
      .update({ completed_at: completedAt ?? new Date().toISOString() })
      .eq('id', sessionId)
      .select(SESSION_COLS)
      .single();
    if (error) throw new Error(error.message);
    return sessionToDomain(data);
  },

  async cancel(sessionId: string): Promise<void> {
    const { error } = await supabase.from('workout_sessions').delete().eq('id', sessionId);
    if (error) throw new Error(error.message);
  },

  async copyFromSession(sourceSessionId: string): Promise<WorkoutSession> {
    const userId = await getCurrentProfileId();
    // 1. Fetch source session metadata
    const { data: source, error: sourceError } = await supabase
      .from('workout_sessions')
      .select(SESSION_COLS)
      .eq('id', sourceSessionId)
      .single();
    if (sourceError) throw new Error(sourceError.message);

    // 2. Fetch source sets (skip already-skipped ones — clone meaningful work)
    const { data: sourceSets, error: setsError } = await supabase
      .from('workout_sets')
      .select(SET_COLS)
      .eq('workout_session_id', sourceSessionId)
      .eq('skipped', false)
      .order('order_index', { ascending: true })
      .order('set_index', { ascending: true });
    if (setsError) throw new Error(setsError.message);

    // 3. Create new session — title carries forward, type stays ad_hoc since
    // the program-day relationship doesn't survive a copy
    const titlePrefix = source.title ?? 'Тренировка';
    const { data: newSession, error: newSessionError } = await supabase
      .from('workout_sessions')
      .insert({
        user_id: userId,
        title: `${titlePrefix} (копия)`,
        session_type: 'ad_hoc',
      })
      .select(SESSION_COLS)
      .single();
    if (newSessionError) throw new Error(newSessionError.message);

    // 4. Clone sets — uncompleted, no timestamps. Weight/reps/etc preserved
    // as defaults so user just hits Done to repeat the set.
    if (sourceSets.length > 0) {
      const rows = sourceSets.map((s) => ({
        workout_session_id: newSession.id,
        exercise_id: s.exercise_id,
        order_index: s.order_index,
        set_index: s.set_index,
        weight: s.weight,
        reps: s.reps,
        duration_seconds: s.duration_seconds,
        distance_meters: s.distance_meters,
        completed: false,
      }));
      const { error: insertError } = await supabase.from('workout_sets').insert(rows);
      if (insertError) throw new Error(insertError.message);
    }

    return sessionToDomain(newSession);
  },

  async setSessionDate(sessionId: string, newDate: string): Promise<WorkoutSession> {
    // Preserve duration: shift both timestamps by the same delta.
    const { data: existing, error: existingError } = await supabase
      .from('workout_sessions')
      .select('started_at, completed_at')
      .eq('id', sessionId)
      .single();
    if (existingError) throw new Error(existingError.message);

    const newStartedAt = new Date(`${newDate}T12:00:00`).toISOString();
    let newCompletedAt: string | null = null;
    if (existing.completed_at !== null) {
      const oldStartedMs = new Date(existing.started_at).getTime();
      const oldCompletedMs = new Date(existing.completed_at).getTime();
      const durationMs = Math.max(0, oldCompletedMs - oldStartedMs);
      newCompletedAt = new Date(new Date(newStartedAt).getTime() + durationMs).toISOString();
    }

    const update: { started_at: string; completed_at?: string | null } = {
      started_at: newStartedAt,
    };
    if (newCompletedAt !== null) update.completed_at = newCompletedAt;

    const { data, error } = await supabase
      .from('workout_sessions')
      .update(update)
      .eq('id', sessionId)
      .select(SESSION_COLS)
      .single();
    if (error) throw new Error(error.message);
    return sessionToDomain(data);
  },

  async addExercise(
    sessionId: string,
    exerciseId: string,
    setsCount: number,
    defaults?: SetDefaults,
  ): Promise<WorkoutSet> {
    // Compute the next order_index within this session (one per exercise).
    const { data: existing, error: existingError } = await supabase
      .from('workout_sets')
      .select('order_index')
      .eq('workout_session_id', sessionId)
      .order('order_index', { ascending: false })
      .limit(1);
    if (existingError) throw new Error(existingError.message);
    const orderIndex = (existing[0]?.order_index ?? 0) + 1;

    const count = Math.max(1, Math.min(20, Math.floor(setsCount)));
    const rows = Array.from({ length: count }, (_, i) => ({
      workout_session_id: sessionId,
      exercise_id: exerciseId,
      order_index: orderIndex,
      set_index: i + 1,
      weight: defaults?.weight ?? null,
      reps: defaults?.reps ?? null,
      duration_seconds: defaults?.durationSeconds ?? null,
      distance_meters: defaults?.distanceMeters ?? null,
      completed: false,
    }));

    const { data, error } = await supabase
      .from('workout_sets')
      .insert(rows)
      .select(SET_COLS)
      .order('set_index', { ascending: true });
    if (error) throw new Error(error.message);
    const first = data[0];
    if (!first) throw new Error('Не удалось создать подходы');
    return setToDomain(first);
  },

  async getLastCompletedSet(exerciseId: string): Promise<WorkoutSet | null> {
    const userId = await getCurrentProfileId();
    // Join sessions to filter by owner; order by completed_at descending so
    // we always grab the most recent finished set the user logged for this
    // exercise. Skipped sets are excluded — they aren't representative.
    const { data, error } = await supabase
      .from('workout_sets')
      .select(`${SET_COLS}, workout_sessions!inner(user_id)`)
      .eq('exercise_id', exerciseId)
      .eq('completed', true)
      .eq('skipped', false)
      .eq('workout_sessions.user_id', userId)
      .order('completed_at', { ascending: false, nullsFirst: false })
      .limit(1);
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) return null;
    const row = data[0];
    if (!row) return null;
    return setToDomain(row);
  },

  async addSet(sessionId: string, exerciseId: string): Promise<WorkoutSet> {
    // Find existing sets for this (session, exercise) to copy defaults from
    // the most recent one (a common gym pattern: same weight, same reps).
    const { data: existing, error: existingError } = await supabase
      .from('workout_sets')
      .select(SET_COLS)
      .eq('workout_session_id', sessionId)
      .eq('exercise_id', exerciseId)
      .order('set_index', { ascending: false })
      .limit(1);
    if (existingError) throw new Error(existingError.message);

    const last = existing[0];
    if (!last) {
      throw new Error('Нет предыдущего подхода для копирования');
    }

    const { data, error } = await supabase
      .from('workout_sets')
      .insert({
        workout_session_id: sessionId,
        exercise_id: last.exercise_id,
        order_index: last.order_index,
        set_index: last.set_index + 1,
        weight: last.weight,
        reps: last.reps,
        duration_seconds: last.duration_seconds,
        distance_meters: last.distance_meters,
        completed: false,
      })
      .select(SET_COLS)
      .single();
    if (error) throw new Error(error.message);
    return setToDomain(data);
  },

  async updateSet(setId: string, patch: SetPatch): Promise<WorkoutSet> {
    const update: TablesUpdate<'workout_sets'> = {};
    if (patch.weight !== undefined) update.weight = patch.weight;
    if (patch.reps !== undefined) update.reps = patch.reps;
    if (patch.durationSeconds !== undefined) update.duration_seconds = patch.durationSeconds;
    if (patch.distanceMeters !== undefined) update.distance_meters = patch.distanceMeters;
    if (patch.rpe !== undefined) update.rpe = patch.rpe;
    if (patch.completed !== undefined) {
      update.completed = patch.completed;
      update.completed_at = patch.completed ? new Date().toISOString() : null;
    }
    if (patch.skipped !== undefined) update.skipped = patch.skipped;

    const { data, error } = await supabase
      .from('workout_sets')
      .update(update)
      .eq('id', setId)
      .select(SET_COLS)
      .single();
    if (error) throw new Error(error.message);
    return setToDomain(data);
  },

  async startSet(setId: string): Promise<WorkoutSet> {
    const { data, error } = await supabase
      .from('workout_sets')
      .update({ started_at: new Date().toISOString() })
      .eq('id', setId)
      .select(SET_COLS)
      .single();
    if (error) throw new Error(error.message);
    return setToDomain(data);
  },

  async deleteSet(setId: string): Promise<void> {
    const { error } = await supabase.from('workout_sets').delete().eq('id', setId);
    if (error) throw new Error(error.message);
  },
};
